import { useState } from "react";
import { toast } from "react-toastify";
import { handleAttachmentPaste } from "utils/attachmentClipboard";
import { uploadErrorMessage, uploadFileToStorage } from "utils/fileUploadService";

/**
 * Adds attachments to a note or comment draft that carries links. Picked or pasted files upload
 * straight away, copied attachments are added as they are; either way the draft only holds links.
 * `apply(links)` appends the new links to the draft, `max` caps the total and `noun` names the
 * draft in messages.
 */
export const useAttachmentUploads = ({ max = Infinity, noun = "note" } = {}) => {
  const [isUploading, setIsUploading] = useState(false);

  const roomFor = (current, incoming) => {
    const room = max - current.length;
    if (room <= 0) {
      toast.error(`A ${noun} can carry at most ${max} attachments.`);
      return 0;
    }
    if (incoming > room) {
      toast.error(`Only ${room} more attachment${room === 1 ? "" : "s"} can be added here.`);
    }
    return room;
  };

  const uploadFiles = async (files, current, apply) => {
    if (!files.length) return;
    const room = roomFor(current, files.length);
    if (!room) return;

    setIsUploading(true);
    const uploaded = [];
    for (const file of files.slice(0, room)) {
      try {
        uploaded.push(await uploadFileToStorage(file));
      } catch (error) {
        toast.error(uploadErrorMessage(error));
      }
    }
    setIsUploading(false);
    if (uploaded.length) apply(uploaded);
  };

  const addLinks = (links, current, apply) => {
    const fresh = links.filter((link) => !current.includes(link));
    if (!fresh.length) {
      toast.info("That attachment is already added.");
      return;
    }
    const room = roomFor(current, fresh.length);
    if (room) apply(fresh.slice(0, room));
  };

  return { isUploading, uploadFiles, addLinks };
};

/**
 * Paste support for a draft kept in the UploadFile shape, `{ attachments: [links] }`. Returns the
 * onPaste handler for the note's text box and whether a pasted file is still uploading.
 */
export const useDraftAttachmentPaste = ({ draft, setDraft, enabled = true, noun = "note" }) => {
  const { isUploading, uploadFiles, addLinks } = useAttachmentUploads({ noun });
  const current = draft?.attachments || [];

  const append = (added) =>
    setDraft((prev) => ({
      ...(prev || {}),
      attachments: [...(prev?.attachments || []), ...added],
    }));

  const onPaste = (event) => {
    if (!enabled || isUploading) return;
    handleAttachmentPaste(event, {
      onFiles: (files) => uploadFiles(files, current, append),
      onLinks: (links) => addLinks(links, current, append),
    });
  };

  return { isUploading, onPaste };
};
